/**
 * AuthProvider — runs on mount to restore session from server (/auth/me).
 * Handles OAuth callback params (access_token in URL hash).
 */
import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi, tokenStore } from '@/lib/api'
import { useAppStore } from '@/store'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setUser, setAuthLoading, logout } = useAppStore()
  const navigate = useNavigate()

  const restoreSession = useCallback(async () => {
    setAuthLoading(true)
    try {
      // Check for OAuth callback token in URL hash
      const hash = window.location.hash
      if (hash.includes('access_token=')) {
        const params = new URLSearchParams(hash.slice(1))
        const token = params.get('access_token')
        const refreshToken = params.get('refresh_token')
        if (token) {
          tokenStore.set(token)
          if (refreshToken) tokenStore.setRefresh(refreshToken)
          // Clean the hash from URL immediately (no React re-render)
          window.history.replaceState(null, '', window.location.pathname)
        }
      }

      // Fetch current user — this validates the token
      const user = await authApi.me()
      setUser(user)
    } catch {
      // No valid session
      tokenStore.clear()
      setUser(null)
    } finally {
      setAuthLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUser, setAuthLoading, navigate])

  useEffect(() => {
    restoreSession()
  }, [restoreSession])

  // Expose logout globally for use in API error handler
  useEffect(() => {
    const PUBLIC_PATHS = ['/', '/login', '/pricing', '/terms', '/privacy']
    const handleLogout = () => {
      logout()
      tokenStore.clear()
      if (!PUBLIC_PATHS.includes(window.location.pathname)) {
        navigate('/login', { replace: true })
      }
    }
    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [logout, navigate])

  return <>{children}</>
}
